import type { Site } from '@hotjar/site';
import { openPlanComparisonModal } from '@hotjar/webapp/features/Billing/PlanComparisonModal/PlanComparisonModal';
import { toPlansAndBillingWithContext } from '@hotjar/webapp/features/Billing/ContextualBanner/utils';
import { PBBannerKinds, PBReferrers } from '@hotjar/webapp/features/Billing/ContextualBanner/types';
import type {
  KnownReferrer,
  PlanComparisonReferrer,
  TrafficRecommendationReferrer,
} from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { logError } from '@hotjar/insights-common/lib/logger';
import { knownReferrers } from '@hotjar/webapp/features/Billing/PlanComparisonModal/constants';
import { openTrafficRecommendationModal } from '@hotjar/webapp/features/Billing/TrafficRecommendationModal/TrafficRecommendationModal';
import { openMultiProductsModal } from '@hotjar/webapp/features/Billing/MultiProductUpsellModals/MultiProductWrapper/MultiProductWrapper';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import { LockedSubscriptionModal, isUnifiedUpsellingEnabled } from '@hotjar/upsell';
import type { LockedSubscriptionModalProps, Referrer } from '@hotjar/upsell';

import { DatadogActionType, DatadogUpsellType, UpsellType } from './constants';
import { upsellLogger } from '../utils';
import {
  isMultiProductsReferrer,
  isPlanComparisonReferrer,
  isTrafficRecommendationReferrer,
} from './utils';
import { supportsMultipleSku } from '../../common/product/features';

type UpsellData = {
  site: Site;
  referrer: KnownReferrer;
  recommendedPlanId?: string;
  isUpgradeRequest?: boolean;
  additionalTrackingData?: Record<string, string>;
};

const openLockedSubscriptionModal = createGlobalModal<LockedSubscriptionModalProps, void, Error>(
  LockedSubscriptionModal,
);

/**
 * Triggers the upsell flow, which results in one of the upsell modals opening.
 *
 * Required props:
 *  * `site` - Site context of the upsell.
 *  * `referrer` - What triggered the upsell, what is being upsold. This identifies the upsell scenario.
 *
 * Optional props:
 *  * `recommendedPlanId` - Caller can specify a single plan to upsell. If you're not sure what this is, omit it.
 *  * `isUpgradeRequest` - Specifies if the upsell context is an approval of an upgrade request. If you're not sure
 *                          what this is, omit it.
 *
 * Returns a promise that resolves when the upsell flow was concluded, which could mean a number of things:
 *  * upsell checkout finished successfully, plan upgraded
 *  * cancelled anywhere in the flow
 */
export const upsell = async ({
  site,
  referrer,
  recommendedPlanId,
  isUpgradeRequest,
  additionalTrackingData,
}: UpsellData): Promise<void> => {
  // Reject if we did not provide a site
  if (!site) {
    logError(new Error('upsell(): You must pass a site.'));
    return Promise.reject();
  }

  // Reject if we did not provide a referrer
  if (!referrer) {
    logError(new Error('upsell(): You must pass a referrer.'));
    return Promise.reject();
  }

  if (!supportsMultipleSku(site.account)) {
    logError(new Error(`upsell(): Pre-Bet 1 (<=PT8) account upsell`));
    toPlansAndBillingWithContext({
      referrer: PBReferrers.MANAGE_PLANS,
      kind: PBBannerKinds.INFO,
    });
    return Promise.reject();
  }

  /**
   * Unkonwn upsell referrer, let's redirect to the Plans & Billing page with a contextual message.
   * Not an error per se, but let's also log an error to Sentry because we want to be aware if such
   * unknown upsells are happening.
   */
  if (!knownReferrers.includes(referrer)) {
    logError(new Error(`upsell(): Unknown referrer given: ${referrer}`));
    toPlansAndBillingWithContext({
      referrer: PBReferrers.MANAGE_PLANS,
      kind: PBBannerKinds.INFO,
    });
    return Promise.reject();
  }

  if (
    site.lockPlan ||
    site.account.billingCycle === 'yearly' ||
    site.account.billingCycle === 'lifetime'
  ) {
    return openLockedSubscriptionModal({ site });
  }

  /**
   * Default upsell type is the PlansAndBillingRedirection.
   * We don't need to check if the referrer is supported by this because it's a catch-all fallback that can handle
   * any referrer, although with suboptimal UX.
   */
  let upsellType = UpsellType.PlansAndBillingRedirection;

  if (isUnifiedUpsellingEnabled(site)) {
    upsellType = UpsellType.MultiProducts;
  } else {
    // Switch upsellType to MultiProducts if the referrer is supported
    if (isMultiProductsReferrer(referrer)) {
      upsellType = UpsellType.MultiProducts;
    }

    // Switch upsellType to PlanComparison if the referrer is supported
    if (isPlanComparisonReferrer(referrer)) {
      upsellType = UpsellType.PlanComparison;
    }

    // Switch upsellType to TrafficRecommendation if the referrer is supported
    if (isTrafficRecommendationReferrer(referrer)) {
      upsellType = UpsellType.TrafficRecommendation;
    }
  }

  switch (upsellType) {
    case UpsellType.MultiProducts:
      upsellLogger?.info('Multi-product modal', {
        upsellType: DatadogUpsellType.MultiProductWrapper,
        action: DatadogActionType.Trigger,
      });

      // See caveat about return type in `createGlobalUpsellModal`
      await openMultiProductsModal({
        site,
        account: site.account,
        referrer: referrer as Referrer, // TODO: remove cast once we only got Referrer referrers,
      });

      return Promise.resolve();

    case UpsellType.PlanComparison:
      upsellLogger?.info('Upsell triggered', {
        upsellType: DatadogUpsellType.PlanComparison,
        action: DatadogActionType.Trigger,
      });

      // See caveat about return type in `createGlobalUpsellModal`
      await openPlanComparisonModal({
        site,
        account: site.account,
        referrer: referrer as PlanComparisonReferrer,
        recommendedPlanId,
        isUpgradeRequest,
        additionalTrackingData,
      });

      return Promise.resolve();
    case UpsellType.TrafficRecommendation:
      upsellLogger?.info('Upsell triggered', {
        upsellType: DatadogUpsellType.TrafficRecommendation,
        action: DatadogActionType.Trigger,
      });

      // See caveat about return type in `createGlobalUpsellModal`
      await openTrafficRecommendationModal({
        site,
        account: site.account,
        referrer: referrer as TrafficRecommendationReferrer,
        isUpgradeRequest,
        recommendedPlanId,
        additionalTrackingData,
      });

      return Promise.resolve();
    case UpsellType.PlansAndBillingRedirection:
      toPlansAndBillingWithContext({
        referrer: PBReferrers.MANAGE_PLANS,
        kind: PBBannerKinds.INFO,
      });
      return Promise.resolve();
    default:
      /**
       * Virtually impossible to ever run into the default case, but let's declare one anyway to handle the error
       * gracefully. The user will be redirected to the Plans & Billing page with a contextual message.
       */
      logError(new Error(`upsell(): Unexpected upsell type: ${upsellType}`));
      toPlansAndBillingWithContext({
        referrer: PBReferrers.MANAGE_PLANS,
        kind: PBBannerKinds.INFO,
      });
      return Promise.reject();
  }
};
